import React, { useEffect, useState } from "react";
import logo from "icons/smallLogo.svg";
import { CloseIcon } from "icons/payments/CloseIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import {
  addNotification,
  updateUserData,
  useUserData,
} from "store/slices/authSlice";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  changeScheduleName,
  decode,
  downloadPdfFromApi,
  encode,
  formatCurrency,
} from "utils/HelperFunctions";
import Button from "components/form/Button";
import QuestionsFooter from "components/auth/QuestionsFooter";
import {
  emailAcceptQuotes,
  emailDeclineQuotes,
  quotesDetailsAccept,
  quotesDetailsDecline,
  quotesDetailsGet,
} from "store/slices/subscriptionSlice";
import { useParams } from "react-router-dom";
import { DownloadIcon } from "icons/payments/DownloadIcon";
import { useLeadData, usePortalVanityUrl } from "store/slices/dashboardSlice";
import { paymentReceiptDownloadPdf } from "store/slices/paymentsSlice";
import { useTestAttrsFactory, useTid } from "utils/testid";
import { formatDisplayDate } from "utils/DateFormat";

interface QuotesPreviewProps {
  id?: any;
  setId?: any;
  show?: any;
  setShow?: any;
  isSlug?: boolean;
  slug?: string;
  fetchDetails?: () => void;
}

interface QuoteItem {
  priceDetails: {
    frequency: string;
  };
  total_price: string | number;
}
const QuotesPreview = (props: QuotesPreviewProps) => {
  const {
    id,
    setId,
    show,
    setShow,
    isSlug = false,
    slug,
    fetchDetails = () => {},
  } = props;
  const [quoteData, setQuoteData] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { clusterId: cluster_id } = useParams();
  const [isDeclineLoading, setIsDeclineLoading] = useState<boolean>(false);
  const [isAcceptLoading, setIsAcceptLoading] = useState<boolean>(false);
  const clusterId = decode(cluster_id || "", true);
  const dispatch = useAppDispatch();
  const userData = useUserData();
  const [paymentLogo, setPaymentLogo] = useState(logo);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [isQuoteDownload, setIsQuoteDownload] = useState<boolean>(false);
  const accountLeadData = useLeadData();
  const vanityUrl = usePortalVanityUrl();
  const tid = useTid();
  const testAttrs = useTestAttrsFactory();
  const onClose = () => {
    setId("");
    setShow(false);
    if (confirm) {
      fetchDetails();
    }
    setQuoteData({});
    setIsLoading(true);
    setIsDeclineLoading(false);
    setIsAcceptLoading(false);
    setConfirm(false);
  };

  const getQuoteData = async () => {
    setIsLoading(true);
    try {
      let payload: any = { slug };
      let res: any = await dispatch(quotesDetailsGet(payload)).unwrap();
      if (res.body.success) {
        const { customerData, companyLogoDetails, quoteData } = res?.body?.data;
        setQuoteData(quoteData);
        setPaymentLogo(companyLogoDetails?.logo);
        dispatch(
          updateUserData({
            ...userData?.data?.accountData,
            ...customerData,
          })
        );
      }
    } catch (error) {
      console.log("getQuoteData error =====", error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if ((id || slug) && show) {
      getQuoteData();
    }
  }, [id, confirm]);

  const OnSubmit = async (value: string) => {
    try {
      let params = {
        id: id,
      };
      switch (value) {
        case "accept":
          setIsAcceptLoading(true);
          let res: any = await dispatch(quotesDetailsAccept(params)).unwrap();
          if (res?.body?.success) {
            let payload: any = {
              email: userData?.data?.accountData?.email,
              clusterid: clusterId,
              invoice_slug: res?.body?.data?.invoice_id,
              invoice_id: res?.body?.data?.invoice_pid,
            };

            if (res?.body?.data?.wireDetails) {
              payload = { ...payload, ...res?.body?.data?.wireDetails };
            }
            if (!res?.body?.data?.isFutureSub) {
              await dispatch(emailAcceptQuotes(payload));
            }
          }
          break;
        case "decline":
          setIsDeclineLoading(true);
          let result: any = await dispatch(
            quotesDetailsDecline(params)
          ).unwrap();
          if (result?.body?.success) {
            const payload = {
              email: userData?.data?.accountData?.email,
              clusterid: clusterId,
              quote_slug: params.id,
              email_type: "quote_update",
            };
            await dispatch(emailDeclineQuotes(payload));
          }
          break;
        default:
          console.log("value =====", value);
      }
    } catch (error) {
      console.log("OnSubmit error =====", error);
    } finally {
      setIsDeclineLoading(false);
      setConfirm(true);
      setIsAcceptLoading(false);
    }
  };

  const handleDownloadQuote = async () => {
    let quote_slug = quoteData?.url_slug || slug;
    let payload = {
      quote_slug: quote_slug,
      lead_id: accountLeadData?.lead_id,
      cluster_id: clusterId,
      url: `${vanityUrl}quotes/${quote_slug}`,
    };

    await downloadPdfFromApi(
      () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
      `Quote - ${quoteData?.quote_number}.pdf`,
      setIsQuoteDownload
    );
  };

  const recurringTotal: number = (quoteData?.quoteItems as QuoteItem[])
    ?.filter((item: QuoteItem) => item.priceDetails.frequency === "recurring")
    .reduce((sum: number, item: QuoteItem) => {
      const price: number = parseFloat(item.total_price as string) || 0;
      return sum + price;
    }, 0);

  return (
    <div
      className="bg-bgSecondary h-[var(--shell-vh)] overflow-y-auto pt-[70px]"
      {...testAttrs("quote-preview", "Quote preview")}
    >
      <div
        className="lg:px-10 px-5 py-5 bg-bgPrimary flex items-center justify-between fixed top-0 w-full z-[3]"
        {...testAttrs("quote-preview-header")}
      >
        <div className="flex items-center gap-2">
          <EyeIcon
            aria-hidden="true"
            strokeColor="var(--color-txt-primary)"
            width="24"
            height="24"
          />
          <div className="lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
            Preview
          </div>
        </div>
        <div className="flex items-center gap-5">
          <div
            className="flex items-center gap-2 cursor-pointer"
            role="button"
            tabIndex={0}
            {...testAttrs("download-quote", "Download quote")}
            onClick={() => {
              handleDownloadQuote();
            }}
          >
            {isQuoteDownload ? (
              <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
            ) : (
              <DownloadIcon
                aria-hidden="true"
                strokeColor="var(--color-btn-primary-active)"
                width="16"
                height="16"
              />
            )}
            <span className="text-sm font-medium text-btnPrimaryActive hover:!text-btnPrimaryHover leading-normal underline">
              {isQuoteDownload ? "Downloading..." : "Download"}
            </span>
          </div>
          <div
            role="button"
            tabIndex={0}
            {...testAttrs("close-quote-preview", "Close quote preview")}
            onClick={() => onClose()}
            className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          >
            <CloseIcon aria-hidden="true" />
          </div>
        </div>
      </div>
      {isLoading && (
        <div className="lg:px-10 px-5 py-4  lg:text-base text-sm bg-white">
          Quote <Skeleton width={150} />
        </div>
      )}
      {!isLoading &&
        (quoteData?.status_quote === "canceled" ||
          quoteData?.status_quote === "cancelled" ||
          quoteData?.status_quote === "declined" ||
          quoteData?.status_quote === "accepted" ||
          quoteData?.status_quote === "expired" ||
          quoteData?.status_quote === "open") && (
          <div
            className={`lg:px-10 px-5 py-4 lg:text-base text-sm ${
              quoteData?.status_quote === "canceled" ||
              quoteData?.status_quote === "cancelled" ||
              quoteData?.status_quote === "declined" ||
              quoteData?.status_quote === "expired"
                ? "bg-lightRed2"
                : quoteData?.status_quote === "open"
                  ? "bg-white border-t-[5px] border-strokePrimary"
                  : "bg-lightGreen"
            }`}
          >
            Quote{" "}
            {quoteData?.status_quote === "open"
              ? "expires"
              : quoteData?.status_quote === "cancelled"
                ? "canceled"
                : quoteData?.status_quote}{" "}
            on{" "}
            <span
              className={`inline-block whitespace-nowrap ${
                quoteData?.status_quote === "canceled" ||
                quoteData?.status_quote === "cancelled" ||
                quoteData?.status_quote === "declined" ||
                quoteData?.status_quote === "expired"
                  ? "text-red"
                  : quoteData?.status_quote === "open"
                    ? "text-blue"
                    : "text-green"
              }`}
            >
              {moment(
                quoteData?.status_quote === "canceled" ||
                  quoteData?.status_quote === "cancelled" ||
                  quoteData?.status_quote === "declined"
                  ? quoteData?.canceled_date
                  : quoteData?.status_quote === "expired"
                    ? quoteData?.expiry_date
                    : quoteData?.status_quote === "open"
                      ? quoteData?.expiry_date
                      : quoteData?.updated_at
              ).format("MMMM D, YYYY")}
            </span>
          </div>
        )}
      <div className="py-[16px] lg:px-10 px-5 flex items-center justify-between">
        <div className="lg:text-xl text-lg font-normal leading-normal text-txtPrimary">
          Quote
        </div>
        <div className="flex items-center justify-end gap-[10px] ">
          {isLoading ? (
            <Skeleton width={170} height={35} />
          ) : paymentLogo ? (
            <div className="h-16 lg:h-16 flex justify-end">
              <img
                src={paymentLogo}
                alt="Company logo"
                className="animate__animated animate__fadeIn object-contain"
              />
            </div>
          ) : (
            <img
              src={logo}
              alt="Pay portal logo"
              className="animate__animated animate__fadeIn"
            />
          )}
          {!paymentLogo && (
            <div className="text-purple font-medium text-xl leading-[30px]">
              CompanyName
            </div>
          )}
        </div>
      </div>
      <div className="w-full">
        <div className="w-full py-2">
          <div className="flex justify-between pb-2 lg:px-10 px-5 items-center">
            <p className="text-btnPrimaryActive text-xl font-normal">
              {isLoading ? (
                <Skeleton width={150} />
              ) : (
                `${
                  userData?.data?.accountData?.first_name +
                    " " +
                    userData?.data?.accountData?.last_name || ""
                }`
              )}
            </p>
            <p className="flex text-sm text-txtPrimary">
              {" "}
              {isLoading ? (
                <Skeleton width={70} />
              ) : (
                `${userData?.data?.accountData.accountid}`
              )}
            </p>
          </div>
          <div className=" lg:px-10 px-5 flex justify-between gap-1">
            <div className="w-full flex text-sm gap-1 break-words text-txtSecondary">
              Balance
              <p className="text-txtPrimary">
                {isLoading ? (
                  <Skeleton width={70} />
                ) : (
                  formatCurrency(userData?.data?.accountData?.balance) ||
                  "$0.00"
                )}
              </p>
            </div>
            <div className="w-full flex text-sm gap-1 justify-end text-txtSecondary">
              <p className="w-full break-words text-end">
                Description{" "}
                <span className="text-txtPrimary">
                  {isLoading ? (
                    <Skeleton width={70} />
                  ) : (
                    `${userData?.data?.accountData?.description || "--"}`
                  )}
                </span>
              </p>
            </div>
          </div>
        </div>
        <div className="py-2 lg:px-10 px-5">
          <div className="w-full h-[1px] bg-strokePrimary "></div>
        </div>
      </div>
      <div className="py-2 lg:px-10 px-5 flex items-center justify-between flex-col gap-1">
        <div className="flex justify-between items-center w-full">
          <span className="text-xs font-medium uppercase leading-[15.62px] w-full text-txtSecondary">
            Amount
          </span>

          <span className="text-xs font-medium uppercase leading-[15.62px] w-full text-txtSecondary text-right">
            VALID UNTIL
          </span>
        </div>
        <div className="flex justify-between items-center w-full">
          <span className="text-2xl leading-[31.25px] w-full text-txtPrimary">
            {isLoading ? (
              <Skeleton width={100} height={25} />
            ) : (
              formatCurrency(quoteData?.total_amount) || "$0.00"
            )}
          </span>

          <span className="text-sm leading-[31.25px] w-full text-txtPrimary text-right">
            {isLoading ? (
              <Skeleton width={100} height={25} />
            ) : (
              moment(quoteData?.expiry_date).format("MM/DD/YYYY")
            )}
          </span>
        </div>
      </div>

      <div className="py-2 lg:px-10 px-5 flex items-center justify-between flex-col w-full">
        <div className="border border-strokePrimary rounded-lg py-5 lg:px-6 px-4 bg-bgPrimary w-full flex flex-col gap-5">
          <div className="w-full flex items-center justify-between text-sm uppercase text-txtPrimary leading-[18.23px]">
            <span className="font-bold">
              Quote #
              {isLoading ? (
                <Skeleton width={70} />
              ) : (
                quoteData?.quote_number || ""
              )}
            </span>
          </div>
          <div className="w-full h-[1px] bg-strokePrimary" />
          {quoteData?.subs_start_date && quoteData?.sub_end_date && (
            <div className="flex w-full justify-between items-center text-sm text-txtSecondary">
              {isLoading ? (
                <Skeleton width={100} />
              ) : (
                <span>
                  {formatDisplayDate(quoteData?.subs_start_date, "MMM D")} -{" "}
                  {formatDisplayDate(quoteData?.sub_end_date, "MMM D, YYYY")}
                </span>
              )}
            </div>
          )}
          {isLoading &&
            [1, 2, 3]?.map((_, index) => (
              <div className="w-full" key={index}>
                <div className="flex justify-between items-center text-txtPrimary text-sm font-medium leading-[18.23px]">
                  <span>
                    <Skeleton width={50} />
                  </span>
                  <span>
                    <Skeleton width={70} />
                  </span>
                </div>
                <div className="flex justify-between items-center text-txtSecondary text-sm leading-[18.23px]">
                  <span className="flex">
                    Qty <Skeleton width={30} />
                  </span>
                  <span className="flex">
                    <Skeleton width={40} />
                    each
                  </span>
                </div>
              </div>
            ))}
          {quoteData?.quoteItems?.length > 0 &&
            !isLoading &&
            [...(quoteData?.quoteItems || [])]
              .sort((a: any, b: any) => {
                const aw = a?.priceDetails?.frequency === "recurring" ? 1 : 0;
                const bw = b?.priceDetails?.frequency === "recurring" ? 1 : 0;
                return bw - aw;
              })
              .map((item: any, index: number) => {
                const itemKey =
                  item?.id ||
                  item?.productDetails?.id ||
                  item?.productDetails?.name ||
                  index;
                return (
                  <div
                    className="w-full"
                    key={itemKey}
                    data-testid={tid(`quote-item-${itemKey}`)}
                  >
                    <div className="flex justify-between items-center text-txtPrimary text-sm font-medium leading-[18.23px]">
                      <span>{item?.productDetails?.name}</span>
                      <span>
                        {formatCurrency(item?.total_price) || "$0.00"}{" "}
                        {item?.priceDetails?.schedule &&
                          item?.priceDetails?.schedule !== "" &&
                          `/ ${changeScheduleName(item?.priceDetails)}`}{" "}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-txtSecondary text-sm leading-[18.23px]">
                      <span>Qty {item?.quantity || 1}</span>
                      <span>
                        {formatCurrency(item?.unit_price) || "$0.00"} each
                      </span>
                    </div>
                  </div>
                );
              })}

          <div className="w-full h-[1px] bg-strokePrimary mt-[10px]" />
          <div className="flex w-full justify-between items-center text-sm uppercase text-txtPrimary leading-[18.23px] font-bold">
            <span>{recurringTotal > 0 ? "Upfront total" : "Subtotal"}</span>
            {isLoading ? (
              <Skeleton width={100} />
            ) : (
              <span className="text-lg">
                {formatCurrency(quoteData?.total_amount) || "$0.00"}
              </span>
            )}
          </div>
          {/* <div className="w-full h-[1px] bg-strokePrimary" /> */}
          <div className="flex flex-col gap-4 w-full justify-between items-center">
            <div className="flex w-full justify-between items-center text-sm uppercase text-txtPrimary leading-[18.23px] font-bold">
              <span>{recurringTotal > 0 ? "Recurring total" : "Total"}</span>
              {isLoading ? (
                <Skeleton width={150} />
              ) : (
                <span className="text-lg">
                  {formatCurrency(
                    recurringTotal > 0
                      ? recurringTotal
                      : quoteData?.total_amount
                  ) || "$0.00"}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {quoteData?.status_quote === "open" && !isLoading && (
        <div className="lg:px-10 px-5 py-2 w-full flex gap-5 justify-between items-center">
          <Button
            text="Decline"
            variant="white"
            className="w-full"
            isLoading={isDeclineLoading}
            isDisabled={isAcceptLoading}
            {...testAttrs("decline-quote", "Decline quote")}
            onClick={() => {
              OnSubmit("decline");
            }}
          />
          <Button
            text="Accept"
            className="w-full"
            variant="variable"
            isLoading={isAcceptLoading}
            isDisabled={isDeclineLoading}
            {...testAttrs("accept-quote", "Accept quote")}
            onClick={() => {
              OnSubmit("accept");
            }}
          />
        </div>
      )}
      {!isLoading && (
        <div className={`py-5 lg:px-10 px-5 bottom-0 flex justify-center `}>
          <QuestionsFooter isPreview={true} />
        </div>
      )}
    </div>
  );
};

export default QuotesPreview;
